import { useCallback, useEffect } from 'react';

import {
  useApiGet,
  useApiPost,
  useApiPatch,
  useApiDelete
} from '../../common/api/Api.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { TrainingUser } from './Training.interface';

export function useGetTrainingUsers(
  trainingId: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<TrainingUser>
  >(`trainings/${trainingId}/users`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { trainingUsers: data, isLoading, error, get };
}

export function useGetTrainingUser(
  trainingId: number,
  trainingVersionId: number,
  userId: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<TrainingUser>(
    `trainings/${trainingId}/version/${trainingVersionId}/users/${userId}`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { trainingUser: data, isLoading, error, getTrainingUser: get };
}

export function useAssignTrainingUsers(trainingId: number) {
  const { data, isLoading, error, post } = useApiPost<TrainingUser>(
    `trainings/${trainingId}/assign`
  );
  const assignUsers = useCallback(
    (dto: {
      users: { id: number }[];
      duration?: number;
      trainingVersionId?: number;
    }) => post(dto),
    [post]
  );
  return {
    assignedUsers: data,
    isAssigning: isLoading,
    error,
    assignUsers
  };
}

export function useDeleteTrainingUsers(
  trainingId: number,
  trainingVersionId: number
) {
  const { data, isLoading, error, del } = useApiDelete<TrainingUser>(
    `trainings/${trainingId}/versions/${trainingVersionId}/users`
  );
  return {
    deletedUsers: data,
    isDeleting: isLoading,
    error,
    del
  };
}

export function useUpdateTrainingUser(
  trainingId: number,
  trainingVersionId: number,
  userId: number
) {
  const { data, isLoading, error, patch } = useApiPatch<TrainingUser>(
    `trainings/${trainingId}/version/${trainingVersionId}/users/${userId}`
  );
  return { trainingUser: data, isLoading, error, patch };
}
